import { FC, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DataSchema } from "common";
import IconBookmark from "@/assets/images/icon_bookmark.svg";
import IconBookmarkFilled from "@/assets/images/icon_bookmark_filled.svg";
import IconThumbsUp from "@/assets/images/icon_thumbs_up.svg";
import { TotalDocumentGoodState } from "./SearchPage.hooks";
import { formatToKilo } from "@/utils/numberFormat";
import { Toggle } from "@/components/ui/toggle";
import { useAtomValue } from "jotai/index";
import { categoryLabelsAtoms } from "@/atoms/form";
import { DocumentVersionTag } from "@/components/DocumentVersionTag";

type SearchResultItemProps = {
  data: DataSchema.SearchResultItem;
  bookmarksSet: Set<string>;
  onClickBookmark: (data: DataSchema.SearchResultItem, bookmarkOn: boolean) => void;
  onClickGood: (documentId: string) => void;
  onClickDocumentTitle: (data: DataSchema.SearchResultItem) => void;
  totalDocumentsGood: TotalDocumentGoodState[];
  goodDocumentIdsSet: Set<string>;
};

export const SearchResultItem: FC<SearchResultItemProps> = (props) => {
  const {
    data,
    bookmarksSet,
    onClickBookmark,
    onClickGood,
    onClickDocumentTitle,
    totalDocumentsGood,
    goodDocumentIdsSet,
  } = props;

  const categoryLabels = useAtomValue(categoryLabelsAtoms);
  const linkTo = useMemo(() => {
    const params = new URLSearchParams();
    params.append("page", data.excerptPageNo);
    return `/documents/${data.documentId}?${params.toString()}`;
  }, [data]);

  const bookmarkOn = bookmarksSet.has(`${data.excerptPageNo || "0"}:${data.documentId}`);

  const goodOn = goodDocumentIdsSet.has(data.documentId);
  const totalDocumentGood = totalDocumentsGood.find((documentGood) => documentGood.id === data.documentId);
  const totalGood = totalDocumentGood?.total;
  const hasTotalGood = totalGood !== undefined;

  return (
    <div className="flex w-[calc(100vw-32px)] flex-col gap-2 sm:w-[880px] sm:max-w-[calc(100vw-320px)]">
      <div className="flex flex-row">
        <Button variant={"ghost"} className="h-8 min-w-8 p-0" onClick={() => onClickBookmark(data, bookmarkOn)}>
          {bookmarkOn && <img src={IconBookmarkFilled} className="h-4 w-4" />}
          {!bookmarkOn && <img src={IconBookmark} className="h-4 w-4" />}
        </Button>
        <div className="ml-2 flex flex-col break-all">
          <Link
            className="flex items-center underline-offset-4 hover:underline"
            to={linkTo}
            target="_blank"
            onClick={() => onClickDocumentTitle(data)}
          >
            <p className="mt-0.5 text-balance text-lg">{data.documentTitle}</p>
          </Link>
          {data.categories && data.categories.length > 0 && (
            <div className={"mb-1 flex flex-1 flex-row gap-2"}>
              {data.categories?.map((category) => (
                <span className={"bg-secondary px-2 py-1 text-xs"} key={category}>
                  {categoryLabels[category]}
                </span>
              ))}

              {data.isLatestVersion === false && <DocumentVersionTag isLatestVersion={data.isLatestVersion} />}
            </div>
          )}
          <p
            className="html-content mr-2 mt-1 text-sm opacity-[62%]"
            dangerouslySetInnerHTML={{ __html: data.excerptHtml }}
          />
        </div>
      </div>
      <div className="flex justify-end">
        <Toggle
          variant={"default"}
          data-state={goodOn && "on"}
          className="h-8 min-w-16 gap-1"
          onClick={() => onClickGood(data.documentId)}
        >
          <img src={IconThumbsUp} className="h-4 w-4" />
          <div className="flex w-full justify-end">
            <p>{hasTotalGood && formatToKilo(totalGood)}</p>
          </div>
        </Toggle>
      </div>
    </div>
  );
};
